import { useMemo, useState } from "react";
import { useParams } from "react-router";
import styled from "styled-components";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import CheckmarkIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CheckmarkIconPath"; // NOSONAR
import InformationCircleIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/InformationCircleIconPath"; // NOSONAR
import { Trans, useTranslation } from "@coworker/locales";
import { MediaTask } from "@coworker/types/lib/tasks/media";
import { WidgetDivider } from "@coworker/reusable/components/Widgets";
import { useArticleById } from "../hooks/useArticleById";
import { useArticlesByMediaId } from "../hooks/useArticlesByMediaId";
import { useCreateTask } from "../../../hooks/TaskActions/useCreateTask";
import { useInputPopup } from "../../InputPopup";
import { useMediaById } from "../hooks/useMediaById";
import { useStoreId, useUID } from "../../../core/auth/useLoggedInUser";
import { useTaskStateUpdates } from "../hooks/useTaskStateUpdates";
import { useWorkspacesAction } from "../../../hooks/useWorkspaces";
import { Reasons } from "../constants";
import { AssigneeSelection } from "../../../core/components/widgets/AssigneeSelection";
import {
  getMainAndAlternativeProducts,
  getMediaName,
  getTranslationKeyFromReason,
} from "../tools";
import { insertTaskLink } from "../services/tasks.service";
import {
  isMainProduct,
  isAlternativeForProduct,
} from "../Molecules/MediaArticles.helper";
import { todosAggregation, getHeader } from "./CreateTaskView.helper";
import { ToggleField } from "../../../core/components/widgets/ToggleField";
import { mediaCreateTaskFactory } from "../ObjectFactories/TaskFactory";
import { Article } from "../types/article";
import { TaskLink } from "../types/taskLink";
import { TaskTodos, TaskTodosSummaries } from "../types/taskCreation";
import { Flag } from "../Atoms/Icons/Flag";
import { HeadLine } from "../Atoms/HeadLine";
import { Person } from "../Atoms/Icons/Person";
import { Priority } from "../Atoms/Icons/Priority";
import { Sofa } from "../Atoms/Icons/Sofa";
import { Text } from "../Atoms/Text";
import AddDescription from "../Molecules/AddDescription";
import { TaskCard } from "../Molecules/TaskCard";
import { CreateTaskModal } from "../Organisms/Modals/CreateTaskModal";
import { FullWidthButton } from "../../SkapaButton";
import { DeleteArticleConfirmModal } from "../Organisms/Modals/DeleteArticleConfirmModal";
import { ReplaceProduct } from "../Organisms/ReplaceProduct";
import { TodoListCard } from "../Organisms/TodoListCard";
import { CreateTaskViewSkeleton } from "../Skeletons/CreateTaskViewSkeleton";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import {
  ArticleListItemMobile,
  ListWrapper,
  PaddedContainer,
} from "../styles/styles";
import AttachPhoto from "../../AttachPhoto";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import { Media } from "../types/media";
import { ItemListViewItemBasic } from "@coworker/apprestructured/src/media-maintenance/components/ItemListViewItemBasic/ItemListViewItemBasic";

const WidthFix = styled("div")`
  .list-view-item__action {
    width: auto;
  }
`;
const Container = styled("div")`
  border-bottom: 1px solid var(--grey150);
`;

export function CreateTaskView() {
  const { mediaId, articleId } = useParams();
  const { t } = useTranslation();
  const { push } = useWorkspacesAction();
  const { getInput } = useInputPopup();
  const uid = useUID();
  const storeId = useStoreId();
  const { call: createTask } = useCreateTask();

  const { data: article, isLoading: articleLoading } =
    useArticleById(articleId);
  const { data: existingArticles } = useArticlesByMediaId(mediaId || "");
  const { data: mediaResponse, isLoading: mediaLoading } =
    useMediaById(mediaId);
  const [newItemNo, setNewItemNo] = useState<string>("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [todoSummary, setTodoSummary] = useState<TaskTodosSummaries>([
    { pieces: 1, is_new_article: true, is_ptag: false },
  ]);
  const [removeArticleChecked, setRemoveArticleChecked] = useState(false);

  const [onClick, setOnClick] = useState(false);

  const [localImages, setLocalImages] = useState<string[]>([]);

  const defaultTask = mediaCreateTaskFactory(
    uid,
    mediaId || "",
    articleId || "",
    article?.itemName || "",
    article?.supplierNumber || "",
    article?.dateStamp || "",
    article?.nbrArticles || 0,
    false,
    false,
    false,
    false,
    mediaResponse?.media.name || "",
    Reasons.DAILY_MAINTENANCE,
    article?.productArticleId || "",
    storeId,
    "",
    article?.itemType || "",
    article?.itemColor || "",
    article?.groupName
      ? t("thisArticleCanBeFoundInGroupString", {
          groupName: article.groupName,
        })
      : " ",
    article?.imageSmall || "",
    false,
    ""
  );

  const {
    task,
    setTask,
    togglePriority,
    updateAssignedTo,
    setDescription,
    updateImages,
  } = useTaskStateUpdates(defaultTask);

  const mainAndAlternativeProducts = getMainAndAlternativeProducts(
    mediaResponse?.media as Media
  );

  // For now the default setup of useState does not work. Therefor this line
  // is needed to make sure that the task is initiated with the default task.
  if (
    defaultTask.item_name.length > 0 &&
    defaultTask.item_name !== task.item_name
  ) {
    setTask(defaultTask);
  }

  const existingArticleIds =
    existingArticles?.map(
      (existingArticle) => existingArticle.productArticleId
    ) || [];

  const handleItemChange = (itemNo: string) => {
    setNewItemNo(itemNo);
  };

  const handleTypeOfTask = async () => {
    const response = await getInput("typeOfRoomSettingTask", {
      selectedReason: task.reason,
    });
    if (response) {
      setTask({ ...task, reason: response });
      if (
        (response !== Reasons.DAILY_MAINTENANCE &&
          isMainProduct(
            article?.id ?? "",
            mainAndAlternativeProducts?.mainProductId1 ?? "",
            mainAndAlternativeProducts?.mainProductId2 ?? ""
          )) ||
        isAlternativeForProduct(
          article?.id ?? "",
          mainAndAlternativeProducts?.alternativeProductId1 ?? "",
          mainAndAlternativeProducts?.alternativeProductId2 ?? ""
        )
      ) {
        setOnClick(true);
      }
    }
  };

  async function createTaskByReason(art: Article | null = null) {
    if (!art) {
      return;
    }

    const createTaskWithTaskLink = async (
      task: MediaTask,
      taskLink: TaskLink
    ) => {
      const taskId = await createTask(task, task.images);
      taskLink.taskId = taskId;
      await insertTaskLink(taskLink);
    };

    if (task.reason === Reasons.REPLACE_PRODUCT) {
      await createTaskWithTaskLink(
        {
          ...task,
          new_product_article_id: newItemNo,
          product_article_id: art.productArticleId,
        } as MediaTask,
        {
          articleId,
          mediaId: mediaId,
          storeId,
          productArticleId: art?.productArticleId,
        } as TaskLink
      );
    } else if (task.reason === Reasons.REMOVE_PRODUCT) {
      await createTaskWithTaskLink(task, {
        articleId,
        mediaId: mediaId,
        storeId,
        productArticleId: art.productArticleId,
      } as TaskLink);
    } else {
      for (const summary of todoSummary) {
        await createTaskWithTaskLink(
          {
            ...task,
            ...summary,
          } as MediaTask,
          {
            articleId,
            mediaId: mediaId,
            storeId,
            productArticleId: art.productArticleId,
          } as TaskLink
        );
      }
    }
  }

  const linkbar = useMemo(() => {
    if (!mediaResponse || !article) {
      return <LinkbarLoading />;
    }

    const currentLabel = getHeader(task, t);

    let linkBarItems;
    if (article.groupId && article.groupName) {
      linkBarItems = [
        {
          label: t("mediaMaintenanceString"),
          to: "/mediamaintenance",
        },
        {
          label: mediaResponse.media.name,
          to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=groups-tab`,
        },
        {
          label: article.groupName,
          to: `/mediamaintenance/groups/${article.groupId}?mediaId=${mediaResponse.media.id}`,
        },
        {
          label: article.itemName,
          to: `/mediamaintenance/articles/${article.id}`,
        },
      ];
    } else {
      linkBarItems = [
        {
          label: t("mediaMaintenanceString"),
          to: "/mediamaintenance",
        },
        {
          label: mediaResponse.media.name,
          to: `/mediamaintenance/tabs/${mediaResponse.media.id}?tab=article-list-tab`,
        },
        {
          label: article.itemName,
          to: `/mediamaintenance/medias/${mediaResponse.media.id}/articles/${article.id}/product_article_id/${article.productArticleId}`,
        },
      ];
    }

    if (mediaResponse.parent?.id) {
      linkBarItems.splice(1, 0, {
        label: mediaResponse.parent.name,
        to: `/mediamaintenance/tabs/${mediaResponse.parent.id}?tab=article-list-tab`,
      });
    }

    return (
      <Linkbar items={linkBarItems} currentLabel={currentLabel} currentAsH1 />
    );
  }, [article, mediaResponse, t, task]);

  if (articleLoading || mediaLoading)
    return (
      <FullScreenPopup noPadding linkbar={linkbar}>
        <CreateTaskViewSkeleton />
      </FullScreenPopup>
    );

  return (
    <FullScreenPopup
      noPadding
      actionBarContent={
        <>
          {article && !removeArticleChecked && (
            <FullWidthButton
              text={t("createString")}
              disabled={!task.assigned_team_id}
              onClick={async () => {
                await createTaskByReason(article);
                push("/mediamaintenance/tabs/" + mediaId);
              }}
            />
          )}
          {article && removeArticleChecked && (
            <FullWidthButton
              type={"danger"}
              text={`${t("deleteArticleString")}`}
              onClick={() => setDeleteModalOpen(true)}
              fluid
            />
          )}
          {article && deleteModalOpen && (
            <DeleteArticleConfirmModal
              article={article}
              onModalOpenChange={(modalOpen) => {
                setDeleteModalOpen(modalOpen);
              }}
              postDeleteFunction={async () => {
                push(`/mediamaintenance/tabs/${mediaId}`);
              }}
            />
          )}
        </>
      }
      linkbar={linkbar}
    >
      <CreateTaskModal
        modalVisibility={onClick}
        onModalChange={() => setOnClick(!onClick)}
        onCancel={() => {
          setTask({ ...task, reason: Reasons.DAILY_MAINTENANCE });
          setOnClick(false);
        }}
      />
      {mediaResponse && article && (
        <>
          <TaskCard
            leftIcon={<Flag />}
            headLine={<Text text={t("typeOfTaskString")} bold />}
            subText={
              <Text
                text={t(getTranslationKeyFromReason(task.reason))}
                italic
                grey
              />
            }
            control={"navigational"}
            showDivider={false}
            onClick={handleTypeOfTask}
          />
          <ListWrapper>
            <ArticleListItemMobile>
              <ItemListViewItemBasic
                media={mediaResponse.media}
                itemNo={article.productArticleId}
                article={article}
                canCopyItemNo={true}
              />
            </ArticleListItemMobile>
          </ListWrapper>
          <WidgetDivider />
          {task.reason === Reasons.REPLACE_PRODUCT && (
            <ReplaceProduct
              numberBackup={
                article.id === mediaResponse.media.mainProductId1 ? 1 : 2
              }
              handleItemChange={handleItemChange}
              oldProductArticleIds={existingArticleIds}
            />
          )}
          {task.reason === Reasons.DAILY_MAINTENANCE && (
            <TodoListCard
              maxPieces={article.nbrArticles}
              todosChanged={(todos: TaskTodos) => {
                setTodoSummary(todosAggregation(todos));
              }}
            />
          )}
          {task.reason === Reasons.REMOVE_PRODUCT && (
            <WidthFix>
              <FixaListView id="article-removed-list" size={"small"}>
                <FixaListViewItem
                  title={t("markAsRemovedString")}
                  leadingIcon={CheckmarkIconPath}
                  control={"switch"}
                  controlProps={{
                    value: "",
                    checked: removeArticleChecked,
                    subtle: true,
                  }}
                  onChange={() =>
                    setRemoveArticleChecked(!removeArticleChecked)
                  }
                  emphasised
                  inset
                />
              </FixaListView>
            </WidthFix>
          )}
          <WidgetDivider />
          <Container>
            <TaskCard
              leftIcon={<Sofa />}
              headLine={<Text text={t("mediaNameString")} bold />}
              subText={
                <>
                  <HeadLine
                    main={getMediaName(
                      mediaResponse?.parent?.name,
                      mediaResponse?.media?.name
                    )}
                    sub1={mainAndAlternativeProducts.mainProductName1}
                    sub2={mainAndAlternativeProducts.mainProductName2}
                    bold={false}
                  />
                  {article.groupName && (
                    <Text
                      text={`${t("inGroupFirstUppercaseString")}: ${
                        article.groupName
                      }`}
                      italic
                    />
                  )}
                </>
              }
            />
          </Container>
          <AssigneeSelection
            selectedTeamId={task.assigned_team_id}
            selectedUserId={task.assigned_user_id}
            onConfirm={updateAssignedTo}
            image={<Person />}
          />
          <ToggleField
            title={
              <b>
                <Trans>priorityString</Trans>
              </b>
            }
            image={<Priority />}
            onChange={togglePriority}
            value={task.priority_flag}
          />
          <AddDescription
            editDescription={setDescription}
            value={task.description}
          />
          <AttachPhoto
            images={task.images}
            onImageListChange={updateImages}
            folder={"task_images"}
            localImages={localImages}
            setLocalImages={setLocalImages}
            removeBottomBorder={false}
            lblColor="var(--grey900)"
            overridePadding="32px 16px 32px 24px"
          />
          {task.reason === Reasons.DAILY_MAINTENANCE && (
            <PaddedContainer $py={"8px"} $px={"16px"}>
              <FixaInlineMessage
                title={t("whatsNextString")}
                body={t("whatsNextAfterCreatingTaskString")}
                variant={"informative"}
                ssrIcon={InformationCircleIconPath}
                dismissable
              />
            </PaddedContainer>
          )}
        </>
      )}
    </FullScreenPopup>
  );
}
